import { useEffect, useState } from 'react';
import { fetchDepartures, fetchNaviLink, type NaviLink, type StopDepartures, type UiDeparture } from '../api';
import { delayLabel, hhmm, minutesLabel, occupancyLabel, qualityLabel, walkLabel } from '../format';
import { WatchError, pushSupported, watchDeparture } from '../watch';

interface Props {
  stopUid: string;
  stopName: string;
  /** true: このポールの発車のみ表示（地図でポールを直接タップした場合） */
  exact?: boolean;
  userLoc: { lat: number; lon: number } | null;
  walkMinutes: number | null;
  onClose: () => void;
  onSelectTrip: (tripUid: string, serviceDate: string) => void;
}

const REFRESH_MS = 20_000;

export function StopSheet({ stopUid, stopName, exact, walkMinutes, onClose, onSelectTrip }: Props) {
  const [data, setData] = useState<StopDepartures | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setData(null);
    setError(null);
    setLoading(true);
    let stopped = false;
    const ctrl = new AbortController();

    const load = async () => {
      try {
        const res = await fetchDepartures([stopUid], { exact }, ctrl.signal);
        if (stopped) return;
        setData(res);
        setError(null);
      } catch (err) {
        if (stopped || (err instanceof DOMException && err.name === 'AbortError')) return;
        setError('発車情報を取得できませんでした');
      } finally {
        if (!stopped) setLoading(false);
      }
    };
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => {
      stopped = true;
      ctrl.abort();
      clearInterval(t);
    };
  }, [stopUid, exact]);

  // 東急バス停では公式接近情報 (東急バスナビ) への外部リンクを表示する。
  // ODPT に RT フィードが無い事業者の補完 (リンクのみ・二次表示はしない)。
  const [navi, setNavi] = useState<NaviLink | null>(null);
  useEffect(() => {
    setNavi(null);
    const ctrl = new AbortController();
    fetchNaviLink(stopUid, ctrl.signal)
      .then(setNavi)
      .catch(() => {
        /* 補助機能: 失敗時はリンク非表示のまま */
      });
    return () => ctrl.abort();
  }, [stopUid]);

  const walk = walkLabel(walkMinutes);

  return (
    <section className="sheet" aria-label={`${stopName} の発車情報`}>
      <header className="sheet-head">
        <div>
          <h2 className="sheet-title">{stopName}</h2>
          <p className="sheet-sub">
            {walk ? `${walk} · ` : ''}
            {data ? `更新 ${hhmm(data.generatedAt)}` : ''}
          </p>
        </div>
        <button className="close-btn" onClick={onClose} aria-label="閉じる">✕</button>
      </header>

      <div className="sheet-body">
        {loading && !data && <p className="muted">読み込み中…</p>}
        {error && <p className="error">{error}</p>}
        {navi && navi.poles.length <= 1 && (
          <a
            className="navi-link"
            href={navi.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            公式の接近情報を見る（東急バスナビ）↗
          </a>
        )}
        {navi && navi.poles.length > 1 && (
          <div className="navi-link-group">
            <span className="navi-link-label">公式の接近情報（東急バスナビ）:</span>
            {navi.poles.map((p, i) => (
              <a
                key={p.dpmk}
                className="navi-link"
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {poleLabel(p.name, stopName, i)} ↗
              </a>
            ))}
          </div>
        )}
        {data && data.departures.length === 0 && (
          <p className="muted">この後の便はありません（本日の運行終了、またはデータ範囲外です）</p>
        )}
        <ul className="dep-list">
          {data?.departures.map((d) => (
            <DepartureRow
              key={`${d.tripUid}-${d.scheduledDeparture}`}
              d={d}
              stopUid={stopUid}
              walkMinutes={walkMinutes}
              onClick={() => {
                // serviceDate はサーバが departures ごとに保持していないため、
                // 便詳細 API には予定時刻から運行日を導出して渡す
                onSelectTrip(d.tripUid, serviceDateOf(d));
              }}
            />
          ))}
        </ul>
        <p className="quality-legend">
          <span className="dot dot-rt" /> リアルタイム
          <span className="dot dot-pred" /> 予測
          <span className="dot dot-sched" /> 時刻表
        </p>
      </div>
    </section>
  );
}

/** 予定時刻 (JST ISO) から運行日を導出。深夜便 (25 時台等) はサーバ側で ISO が翌日日付になるため、そのまま日付部分を使わず 3 時までは前日に丸める。 */
function serviceDateOf(d: UiDeparture): string {
  const dep = new Date(d.scheduledDeparture);
  const jst = new Date(dep.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
  if (jst.getHours() < 3) jst.setDate(jst.getDate() - 1);
  const y = jst.getFullYear();
  const m = String(jst.getMonth() + 1).padStart(2, '0');
  const day = String(jst.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type WatchState = 'idle' | 'busy' | 'set' | 'error';

function DepartureRow({
  d,
  stopUid,
  walkMinutes,
  onClick,
}: {
  d: UiDeparture;
  stopUid: string;
  walkMinutes: number | null;
  onClick: () => void;
}) {
  const delay = delayLabel(d.delaySeconds);
  const occupancy = occupancyLabel(d.occupancyStatus);
  const [watch, setWatch] = useState<WatchState>('idle');
  const [watchMsg, setWatchMsg] = useState<string | null>(null);

  // 「そろそろ出発」= 徒歩時間 + 支度バッファ 2 分 (位置不明時は 5 分前)
  const leadMinutes = walkMinutes != null ? walkMinutes + 2 : 5;
  const canWatch =
    pushSupported() && !d.cancelled && d.minutesUntil > leadMinutes && watch !== 'set';

  const onWatch = async () => {
    if (watch === 'busy') return;
    setWatch('busy');
    setWatchMsg(null);
    try {
      await watchDeparture({
        tripUid: d.tripUid,
        stopUid,
        serviceDate: serviceDateOf(d),
        leadMinutes,
      });
      setWatch('set');
      setWatchMsg(`${leadMinutes}分前に通知します`);
    } catch (err) {
      setWatch('error');
      setWatchMsg(err instanceof WatchError ? err.message : 'ウォッチ登録に失敗しました');
    }
  };

  return (
    <li className="dep-item">
      <button className={`dep-row ${d.cancelled ? 'dep-cancelled' : ''}`} onClick={onClick}>
        <div className="dep-min">
          <span className={`dep-min-num q-${d.quality}`}>
            {d.cancelled ? '運休' : minutesLabel(d.minutesUntil)}
          </span>
          <span className="dep-min-cap">{d.cancelled ? '' : qualityLabel(d.quality)}</span>
        </div>
        <div className="dep-main">
          <div className="dep-headsign">
            {d.routeShortName && (
              <span
                className="route-badge"
                style={{
                  background: d.routeColor ? `#${d.routeColor.replace('#', '')}` : '#334155',
                  color: d.routeTextColor ? `#${d.routeTextColor.replace('#', '')}` : '#fff',
                }}
              >
                {d.routeShortName}
              </span>
            )}
            <strong>{d.headsign}</strong>
          </div>
          <div className="dep-meta">
            {hhmm(d.expectedDeparture ?? d.scheduledDeparture)}発
            {delay && <span className={d.delaySeconds && d.delaySeconds > 60 ? 'late' : 'ontime'}> {delay}</span>}
            {occupancy && <span> · {occupancy}</span>}
            <span className="operator"> · {d.operatorName}</span>
            {watchMsg && (
              <span className={watch === 'error' ? 'watch-msg watch-msg-err' : 'watch-msg'}>
                {' '}· {watchMsg}
              </span>
            )}
          </div>
        </div>
        <div className="dep-chevron">›</div>
      </button>
      {(canWatch || watch === 'set') && (
        <button
          className={`watch-btn ${watch === 'set' ? 'watch-btn-set' : ''}`}
          onClick={() => void onWatch()}
          disabled={watch === 'busy' || watch === 'set'}
          aria-label={
            watch === 'set'
              ? 'この便のウォッチは設定済みです'
              : `この便を出発${leadMinutes}分前に通知`
          }
          title={watch === 'set' ? '設定済み' : `出発${leadMinutes}分前に通知`}
        >
          {watch === 'busy' ? '…' : watch === 'set' ? '🔔✓' : '🔔'}
        </button>
      )}
    </li>
  );
}

/**
 * のりばリンクの表示名。サイトののりば名から停留所名の重複を除いて
 * 「のりば１」等の短い表示にする。残りが番号以外 (方面名など) なら
 * そのまま表示し、空になった場合は「のりば N」で採番する。
 */
function poleLabel(poleName: string, stopName: string, index: number): string {
  const stripped = poleName.replaceAll(stopName, '').replace(/[\s　]+/g, '').trim();
  if (stripped === '') return `のりば${index + 1}`;
  return /^[0-9０-９]+$/.test(stripped) ? `のりば${stripped}` : stripped;
}
